/**
 * moveSourceToRightOfTarget moves an item from one part of the layout to the other, and it moves all the other
 * items to make the new item fit. The "source" item is inserted to the right of the "target" item, which
 * means the target item doesn't move, but all the following items move to fit the source item.
 *
 * For example:
 * Say we want to move "Item 4" to the position where "Item 1" currently is in the following layout:
 * [Item 1][Item 2]
 * [Item 3][Item 4]
 *
 * This function will generate:
 * [Item 1][Item 4]
 * [Item 2][Item 3]
 *
 * @param oldLayout Starting layout
 * @param source The item that we want to move
 * @param target The item that we will insert the source item directly to the right of
 */
import _ from 'underscore';

export default function moveSourceToRightOfTarget<T>(
  oldLayout: Array<Array<T>>,
  source: T,
  target: T,
  comparator: (item1: T, item2: T) => boolean = (item1: T, item2: T) => item1 === item2
): {newLayout: Array<Array<T>>; layoutChanged: boolean} {
  if (source === target) {
    return {
      layoutChanged: false,
      newLayout: oldLayout,
    };
  }

  const firstRowLength = oldLayout[0].length;
  const flatLayout = oldLayout.flat();

  const sourceIndex = flatLayout.findIndex((item) => comparator(source, item));
  flatLayout.splice(sourceIndex, 1);
  const targetIndex = flatLayout.findIndex((item) => comparator(target, item));
  const newSourceIndex = targetIndex + 1;

  flatLayout.splice(newSourceIndex, 0, source);

  return {
    layoutChanged: newSourceIndex !== sourceIndex,
    newLayout: _.chunk(flatLayout, firstRowLength) as Array<Array<T>>,
  };
}
